import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Button,
  Select,
  Space,
  Divider,
  Typography,
  message,
  Input,
  Modal
} from "antd";
import {
  PlayCircleOutlined,
  SaveOutlined
} from "@ant-design/icons";
// import { useParams } from "react-router-dom";

import ApiClient from "./apiClient";
import { validateStage } from "./validation";
import StageInspector from "./StageInspector";
import PipelineResult from "./PipelineResult";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";

const { Text } = Typography;

export default function PipelineBuilder() {
  const [mtObjects, setMtObjects] = useState([]);
  const [fields, setFields] = useState([]);
  const [objectName, setObjectName] = useState("");
  const { id: pipelineId } = useParams(); // undefined for create
  const navigate = useNavigate();

  const [stages, setStages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [result, setResult] = useState(null);
  const [fullScreenResult, setFullScreenResult] = useState(false);

  const [selectedFields, setSelectedFields] = useState([]);
  const [pageSize, setPageSize] = useState(100);

  const selectedStage = stages.find((s) => s.id === selectedId);

  const [saveModal, setSaveModal] = useState(false);
  const [pipelineName, setPipelineName] = useState("");
  const [pipelineApiName, setPipelineApiName] = useState("");
  const [pipelineDescription, setPipelineDescription] = useState("");

  const gridRef = useRef(null);

  useEffect(() => {
    if (pipelineId) {
      loadPipelineForEdit();
    }
  }, [pipelineId]);
  
  // Fetch objects on mount
  useEffect(() => {
    ApiClient.listMtObjects().then(setMtObjects);
  }, []);

  const loadPipelineForEdit = async () => {
    try {
      const resp = await ApiClient.getPipelineById(pipelineId);
  
      // Set objectName from pipeline, if stored in your model
      const objName = resp.object_name || ""; 
      setObjectName(objName);
  
      // Load fields for this object
      if (objName) {
        const f = await ApiClient.listMtFields(objName);
        f.push(
          { name: "created_at", label: "Created Date", type: "datetime", system: true },
          { name: "updated_at", label: "Last Modified Date", type: "datetime", system: true },
          { name: "_id", label: "Id", type: "string", system: true }
        );
        setFields(f);
      }
  
      setPipelineName(resp.name);
      setPipelineApiName(resp.apiname);
      setPipelineDescription(resp.description || "");
      setStages(resp.pipeline_json || []);
  
      message.success("Pipeline loaded");
    } catch (err) {
      console.error(err);
      message.error("Failed to load pipeline");
    }
  };
  
  const generateApiName = (label) => {
    return label
      .replace(/[^a-zA-Z]/g, '') // Remove all characters except letters (a-z, A-Z)
      .replace(/\s+/g, '')       // Remove all spaces
      .trim();
  };

  const loadFields = async (obj) => {
    setObjectName(obj);
    const f = await ApiClient.listMtFields(obj);
    f.push(
      { name: "created_at", label: "Created Date", type: "datetime", system: true },
      { name: "updated_at", label: "Last Modified Date", type: "datetime", system: true },
      { name: "_id", label: "Id", type: "string", system: true }
    );
    setFields(f);
    message.success(`Loaded ${f.length} fields`);
  };

  const addStage = (type,title) => {
    const base = { id: `s_${Date.now()}`, type, config: {}, title:title };
    switch (type) {
        case "$match":
            base.config = { mode: "and", rules: [] };
            break;
        case "$group":
            base.config = {aggregates: [], groupFields: []  };
            break;
        case "$project":
            base.config = { include: [] };
            break;
        case "$aggmatch":
            base.config = { mode: "and", rules: [] };
            break;
        case "$sort":
            base.config = { sortBy: [{ field: null, order: "asc" }] };
            break;
        case "$limit":
            base.config = { skip: 0, limit: 50 };
            break;
        case "$join":
          base.config = {
            mode: "simple",        // "simple" or "advanced"
    
            // For simple mode
            from: null,
            localField: null,
            foreignField: "_id",
            as: "records",
    
            // For advanced mode
            let: {},
            pipeline: [],
            lookupReturnType:"array", // array | single | merge
            // Auto-type aware suggestions
            //relatedFields: [],
            //fromFields: [],
    
            // Prefab templates
            templates: {
              simpleLookup: {
                label: "Simple Lookup",
                apply: (rel) => ({
                  mode: "simple",
                  from: rel.targetObject,
                  localField: rel.fieldName,
                  foreignField: "_id",
                  as: rel.fieldName + "_obj"
                })
              },
    
              pipelineLookup: {
                label: "Pipeline Lookup",
                apply: (rel) => ({
                  mode: "advanced",
                  from: rel.targetObject,
                  let: { parentId: `$${rel.fieldName}` },
                  pipeline: [
                    {
                      $match: {
                        $expr: { $eq: ["$_id", "$$parentId"] }
                      }
                    }
                  ],
                  as: rel.fieldName + "_obj"
                })
              }
            },
    
            // Automatically create $$variable from localField
            autoBind: true,
    
            // Optional auto-unwind toggle
            unwind: false
          };
          break;
        default:
          break;
    }
    setStages([...stages, base]);
  };

  const updateStage = (u) => {
    setStages(stages.map((s) => (s.id === u.id ? u : s)));
  };

  const removeStage = (id) => {
    setStages(stages.filter((s) => s.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const runPipeline = async () => {
    if (!objectName) return message.error("Select an object first");

    for (let s of stages) {
      const v = validateStage(s, fields, stages);
      if (v) return message.error(`Stage ${s.type}: ${v}`);
    }

    const resp = await ApiClient.runPipeline(objectName, stages);
    if (resp?.error) return message.error(resp.message || "Pipeline error");

    setResult(resp);
    setFullScreenResult(true);
    message.success("Pipeline executed!");
  };

  const savePipeline = async () => {
    if (!pipelineName || !pipelineApiName) {
      return message.error("Name and API Name are required");
    }
  
    const payload = {
      name: pipelineName,
      apiname: pipelineApiName,
      description: pipelineDescription,
      pipeline_json: stages,
      object_name: objectName
    };
  
    try {
      let resp;
  
      if (pipelineId) {
        // 🔁 UPDATE
        resp = await ApiClient.updatePipeline(pipelineId, payload);
        message.success("Pipeline updated");
      } else {
        // 🆕 CREATE
        resp = await ApiClient.createPipeline(payload);
        message.success("Pipeline created");
      }
  
      if (resp?.errors) {
        return message.error(resp.errors[0] || "Save failed");
      }
  
      setSaveModal(false);
    } catch (err) {
      console.error(err);
      message.error("Save failed");
    }
  };
  

  const fieldOptions = fields.map((f) => ({ label: f.label, value: f.name }));

  return (
  <>
    {/* HEADER */}
    <Card
      size="small"
      style={{
        marginBottom: 12,
        display: "flex",
        alignItems: "center"
      }}
    >
      <Space>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>

        <Divider type="vertical" />

        <Text strong style={{ fontSize: 16 }}>
          {pipelineId
            ? `Edit Pipeline: ${pipelineName || "Loading..."}`
            : "Create Pipeline"}
        </Text>
      </Space>
    </Card>

    <div style={{ display: "flex", height: "100vh", gap: 12, padding: 12 }}>
      
      {/* LEFT PANEL: Metadata */}
      <Card title="Metadata" size="small" style={{ width: 300 }}>
        <Text strong>Select Object</Text>
        <Select
          style={{ width: "100%", marginTop: 8 }}
          placeholder="Select object"
          options={mtObjects}
          onChange={loadFields}
          value={objectName || undefined}
        />

        <Divider />

        <Text strong>Fields</Text>
        <div style={{ maxHeight: 320, overflowY: "auto", marginTop: 8 }}>
          {fields.map((f) => (
            <div key={f.name} style={{ paddingBottom: 6 }}>
              <Text>{f.label}</Text>
              <br />
              <Text type="secondary">{f.name} • {f.type}</Text>
              <Divider style={{ margin: "6px 0" }} />
            </div>
          ))}
        </div>
      </Card>

      {/* MIDDLE PANEL: Stage Buttons & Run */}
      <Card title="Stages" size="small" style={{ width: 260 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button block onClick={() => addStage("$match", "Filter")}>Filter</Button>
          <Button block onClick={() => addStage("$join", "Join")}>Join</Button>
          <Button block onClick={() => addStage("$project", "Select Fields")}>Select Fields</Button>
          <Button block onClick={() => addStage("$group", "Group")}>Group</Button>
          <Button block onClick={() => addStage("$aggmatch", "Advance Match")}>Advance Match</Button>
          <Button block onClick={() => addStage("$sort", "Sort")}>Sort</Button>
          <Button block onClick={() => addStage("$limit", "Limit")}>Limit</Button>

          <Divider />

          <Button
            type="primary"
            block
            icon={<PlayCircleOutlined />}
            onClick={runPipeline}
          >
            Run Pipeline
          </Button>

          <Button
            type="default"
            block
            icon={<SaveOutlined />}
            onClick={() => setSaveModal(true)}
          >
            {pipelineId ? "Update Pipeline" : "Save Pipeline"}
          </Button>

        </Space>
      </Card>

      {/* PIPELINE LIST */}
      <Card
        title={`Pipeline (${stages.length})`}
        size="small"
        style={{ flex: 1, overflowY: "auto", maxWidth: 600 }}
      >
        {stages.map((s, i) => (
          <Card key={s.id} size="small" type="inner" style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ cursor: "pointer" }} onClick={() => setSelectedId(s.id)}>
                <Text strong>{i + 1}. {s.title}</Text>
                <div style={{ fontSize: 12, color: "#666", wordWrap: "anywhere" }}>
                  {JSON.stringify(s.config)}
                </div>
              </div>

              <Space>
                <Button size="small" onClick={() => setSelectedId(s.id)}>Edit</Button>
                <Button danger size="small" onClick={() => removeStage(s.id)}>X</Button>
              </Space>
            </div>
          </Card>
        ))}
      </Card>

      {/* INSPECTOR */}
      <div style={{ width: 380 }}>
        <StageInspector
          selectedStage={selectedStage}
          fields={fields}
          onUpdate={(u) => setStages(stages.map((s) => (s.id === u.id ? u : s)))}
          pipeline={stages}
          mtObjects={mtObjects}           // list of available objects for lookup "from"
          ApiClient={ApiClient}      // allow inspector to make API calls if needed
          loadFields={loadFields}       // allow inspector to fetch fields for selected object if needed

        />
      </div>

      {/* OUTPUT PANEL */}
      {fullScreenResult && (
        <PipelineResult
          result={result}
          fields={fields}
          objectName={objectName}
          onClose={() => setFullScreenResult(false)}
        />
      )}

      {/* SAVE PIPELINE MODAL */}
      <Modal
        title="Save Pipeline"
        open={saveModal}
        onCancel={() => setSaveModal(false)}
        onOk={savePipeline}
      >
        <Input
          placeholder="Pipeline Name"
          value={pipelineName}
          onChange={(e) => setPipelineName(e.target.value)}
          onBlur={() => {
            // ✅ Generate only on CREATE and only if empty
            if (!pipelineId && !pipelineApiName && pipelineName) {
              setPipelineApiName(generateApiName(pipelineName));
            }
          }}
          style={{ marginBottom: 10 }}
        />

        <Input
          placeholder="API Name"
          value={pipelineApiName}
          disabled={!!pipelineId}
          onChange={(e) => setPipelineApiName(e.target.value)}
          style={{ marginBottom: 10 }}
        />

        <Input.TextArea
          rows={3}
          placeholder="Description (optional)"
          value={pipelineDescription}
          onChange={(e) => setPipelineDescription(e.target.value)}
        />
      </Modal>
    </div>
    </>
  );
}